import dynamic from 'next/dynamic';
import { dynamicImportCatch } from '@/utils/appUtilsClient.tsx';
import { useMediaSSR } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import styled from 'styled-components';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { EvoStayInfo } from '@/app/components/hotel-detail/evo/EvoStayInfo';
import { DebugPanel } from '@/app/components/hotel-detail/DebugPanel';
import {
  EvoTripOverview,
  EvoTripOverviewMobile,
} from '@/app/components/hotel-detail/evo/trip-overview/EvoTripOverview';
import EvoHero from '@/app/components/hotel-detail/evo/Hero/EvoHero';
import { EvoDetailsProviders } from '@/app/components/hotel-detail/evo/EvoDetailsProviders';
import { EvoBreadcrumbs } from '@/app/components/hotel-detail/evo/EvoBreadcrumbs';
import { EvoTripSummaryBar } from '@/app/components/hotel-detail/evo/EvoSubNavigation';
import {
  HotelPageEmissionsSectionEvo,
  useHasSustainabilitySection,
} from '@/app/components/hotel-detail/HotelPageEmissionsSection';
import { HotelPageBeforeYouBookSectionEvo } from '@/app/components/hotel-detail/HotelPageBeforeYouBookSection';
import { AdsFooter } from '@/app/components/hotel-detail/AdsFooter';
import useInitialScrollbarWidth from '@/app/common/hooks/useInitialScrollbarWidth.ts';
import { ApexCalendarFiltersProvider } from '@/app/components/hotel-detail/evo/trip-overview/ApexCalendarFiltersProvider';
import { useApexCalendar } from '@/app/components/hotel-detail/hooks/useApexCalendar';
import { useReportHotelNoAvailability } from '@/app/components/hotel-detail/hooks/useReportHotelNoAvailability';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';

const HotelMapModal = dynamic(() => import('../HotelMapModal').catch(dynamicImportCatch), {
  ssr: true,
});

const HotelProviderSelector = dynamic(() => import('../HotelProviderSelector').catch(dynamicImportCatch), {
  ssr: true,
});

const Container = styled.div`
  background-color: white;
  display: grid;
  padding-bottom: 80px;
  grid-template-areas:
    'telesales'
    'hero'
    'debug'
    'divider-trip-overview'
    'trip-overview-mobile'
    'divider-stay-info'
    'stay-info'
    'divider-emissions'
    'emissions'
    'divider-before-you-book'
    'before-you-book'
    'ad-bottom';

  & > * {
    min-width: 0;
  }

  @media ${customCss.breakpoints.lg} {
    column-gap: 16px;
    grid-template-columns: auto minmax(0, 737px) 423px auto;
    /* The bottom ad shares this row with the trip overview instead of spanning both columns:
       spanning ended the trip-overview area above the ad, so the sticky card scrolled out of
       view as soon as the ads came into it. The card now stays pinned to the end of the grid,
       at the price of a column-width ad — MPH-831 deliberately supersedes MPH-764, which had
       centred the ad across both columns. */
    grid-template-areas:
      'stepper stepper stepper stepper'
      '. telesales telesales .'
      '. hero trip-overview .'
      '. debug trip-overview .'
      '. stay-info trip-overview .'
      '. divider-emissions trip-overview .'
      '. emissions trip-overview .'
      '. divider-before-you-book trip-overview .'
      '. before-you-book trip-overview .'
      '. ad-bottom trip-overview .';
  }

  @media ${customCss.breakpoints.lgPlus} {
    column-gap: 40px;
  }
`;

const Divider = styled.div`
  height: 1px;
  margin: 16px 0;
  background-color: ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};

  @media ${customCss.breakpoints.lg} {
    margin: 32px 0;
  }
`;

export default function EvoDetailsLayout() {
  const { commonData, localization } = usePageWrapperData();
  const { checkin, nights } = useUrlSearchParams();
  useInitialScrollbarWidth();
  // undefined until the viewport is measured after hydration; until then the layout
  // falls back to the UA-based guess, which is wrong on tablets with desktop UAs
  const isLgMeasured = useMediaSSR(customCss.breakpoints.lg);
  const isLgOrGreater = isLgMeasured ?? commonData.isDesktopDevice;
  return (
    <EvoDetailsProviders>
      <ApexCalendarFiltersProvider initialNights={nights} initialCheckin={checkin}>
        <ApexCalendarTrigger />
        <NoAvailabilityReporter />
        <Container>
          {isLgOrGreater && (
            <EvoBreadcrumbs
              activeStep={1}
              style={{ gridArea: 'stepper' }}
              css={`
                /* first paint may render this below lg (UA-based isLgOrGreater default) where
                   the 'stepper' grid area doesn't exist and would create an implicit column */
                display: none;
                margin-bottom: 26px;

                @media ${customCss.breakpoints.lg} {
                  display: flex;
                }
              `}
            />
          )}
          {localization.isTelesales ? <HotelProviderSelector style={{ gridArea: 'telesales' }} /> : null}
          {isLgOrGreater && (
            <EvoTripOverview
              showChangeTransportButton={true}
              style={{ gridArea: 'trip-overview' }}
              css={`
                display: none;
                @media ${customCss.breakpoints.lg} {
                  display: block;
                }
              `}
            />
          )}
          {/* Also mounted next to the desktop card while the viewport is unmeasured: if the
              UA guess was wrong (tablet), the section is already on screen with the same
              loading placeholders its sections use on desktop, so hydration corrects the
              branch without a layout shift. Hidden ≥lg, where its grid areas don't exist
              and would otherwise create implicit tracks. */}
          {(isLgMeasured === undefined || !isLgOrGreater) && (
            <>
              <Divider
                style={{ gridArea: 'divider-trip-overview' }}
                css={`
                  @media ${customCss.breakpoints.lg} {
                    display: none;
                  }
                `}
              />
              <EvoTripOverviewMobile
                css={`
                  @media ${customCss.breakpoints.lg} {
                    display: none;
                  }
                `}
              />
            </>
          )}
          <EvoHero
            css={`
              grid-area: hero;

              @media ${customCss.breakpoints.lg} {
                margin-bottom: 32px;
              }
            `}
          />
          {/* Inset as margin, not `evoSectionGutters`: the panel is a bordered box, so the
              gutter has to move the border too, and the gutters' padding half would override
              the panel's own 16px padding. The values still track the evo sections. */}
          <DebugPanel
            style={{ gridArea: 'debug' }}
            css={`
              margin: 16px;

              @media ${customCss.breakpoints.sm} {
                margin: 16px 24px;
              }

              /* No top margin from lg: EvoHero already carries a 32px bottom margin there. */
              @media ${customCss.breakpoints.lg} {
                margin: 0 0 16px;
              }
            `}
          />
          <HotelMapModal />
          {/* Same unmeasured-viewport treatment as the trip-overview section above. */}
          {(isLgMeasured === undefined || !isLgOrGreater) && (
            <Divider
              style={{ gridArea: 'divider-stay-info' }}
              css={`
                @media ${customCss.breakpoints.lg} {
                  display: none;
                }
              `}
            />
          )}
          <EvoStayInfo style={{ gridArea: 'stay-info' }} />
          <EmissionsDivider />
          <HotelPageEmissionsSectionEvo style={{ gridArea: 'emissions' }} />
          <Divider style={{ gridArea: 'divider-before-you-book' }} />
          <HotelPageBeforeYouBookSectionEvo style={{ gridArea: 'before-you-book' }} />
          <AdsFooter containedFromLg={true} />
        </Container>
        {/* Mounted while the viewport is unmeasured for the same reason as the mobile trip
            overview above; the bar hides itself ≥lg (see EvoSubNavigation). */}
        {(isLgMeasured === undefined || !isLgOrGreater) && <EvoTripSummaryBar />}
      </ApexCalendarFiltersProvider>
    </EvoDetailsProviders>
  );
}

function ApexCalendarTrigger() {
  useApexCalendar();
  return null;
}

function NoAvailabilityReporter() {
  useReportHotelNoAvailability();
  return null;
}

// The emissions section self-hides until its data resolves (and stays hidden for hotels with
// neither an emissions estimate nor a sustainability certification) — its divider must follow it,
// or a stray line sits under the rooms while they load. Split out so the hook runs inside the
// providers the layout renders.
function EmissionsDivider() {
  return useHasSustainabilitySection() ? <Divider style={{ gridArea: 'divider-emissions' }} /> : undefined;
}
